import { atom, selector } from 'recoil'

import { isValidEmail } from 'Helpers/validators'

import type {
  ListsState,
  ModalBulkAddToGroupState,
  ModalBulkRemoveFromGroupsState,
  ModalBulkUnsubscribeState,
  ModalColumnsState,
  ModalDelStatsContactState,
  ModalDeleteState,
  ModalFilterDetailsState,
  ModalSaveSegmentState,
  ModalUnsubscribeState,
} from 'Pages/RecipientsList/model'

import { listsState } from 'Pages/RecipientsList/recoil/state/common'
import { selectionCountState } from 'Pages/RecipientsList/recoil/state/results'

export const modalUnsubscribeState = atom<ModalUnsubscribeState>({
  key: 'recipientsList_modalUnsubscribeState',
  default: {
    show: false,
    idRecipient: undefined,
    channelEmail: false,
    channelSMS: false,
    wipeData: false,
    lists: [],
    allLists: false,
    recipientSubscriptionLists: [],
  },
})

export const modalUnsubscribeCanUnsubscribeState = selector({
  key: 'recipientsList_modalUnsubscribeCanUnsubscribeState',
  get: ({ get }) => {
    const { idRecipient, channelEmail, channelSMS, allLists, lists } = get(
      modalUnsubscribeState,
    )

    const validLists = allLists || lists.length > 0

    return (
      idRecipient !== undefined && (channelEmail || channelSMS) && validLists
    )
  },
})

export const modalUnsubscribeRecipientListsState = selector<
  ListsState['items']
>({
  key: 'recipientsList_modalUnsubscribeRecipientListsState',
  get: ({ get }) => {
    const lists = get(listsState)
    const { recipientSubscriptionLists } = get(modalUnsubscribeState)

    return lists.items.filter((l) =>
      recipientSubscriptionLists.includes(l.value),
    )
  },
})

//

export const modalDeleteState = atom<ModalDeleteState>({
  key: 'recipientsList_modalDeleteState',
  default: {
    show: false,
    idRecipient: undefined,
    mode: undefined,
  },
})

export const modalDeleteCanConfirmState = selector({
  key: 'recipientsList_modalDeleteCanConfirmState',
  get: ({ get }) => {
    const { idRecipient, mode } = get(modalDeleteState)

    return idRecipient !== undefined && mode !== undefined
  },
})

//

export const modalColumnsState = atom<ModalColumnsState>({
  key: 'recipientsList_modalColumnsState',
  default: {
    show: false,
    items: [],
  },
})

export const modalColumnsCanSaveState = selector<boolean>({
  key: 'recipientsList_modalColumnsCanSaveState',
  get: ({ get }) => {
    const { items } = get(modalColumnsState)

    const definedColumns = items.filter((o) => o !== undefined)

    return definedColumns.length > 0
  },
})

export const modalFilterDetailsState = atom<ModalFilterDetailsState>({
  key: 'recipientsList_modalFilterDetailsState',
  default: {
    show: false,
    id: undefined,
    type: undefined,
    description: undefined,
    name: undefined,
  },
})

//  bulk unsubscribe

export const modalBulkUnsubscribeState = atom<ModalBulkUnsubscribeState>({
  key: 'recipientsList_modalBulkUnsubscribeState',
  default: {
    show: false,
    channelEmail: false,
    channelSMS: false,
    wipeData: false,
  },
})

export const modalBulkUnsubscribeCanUnsubscribeState = selector({
  key: 'recipientsList_modalBulkUnsubscribeCanUnsubscribeState',
  get: ({ get }) => {
    const { channelEmail, channelSMS } = get(modalBulkUnsubscribeState)

    return channelEmail || channelSMS
  },
})

export const modalBulkUnsubscribeDisableWipeDataState = selector<boolean>({
  key: 'recipientsList_modalBulkUnsubscribeDisableWipeDataState',
  get: ({ get }) => {
    const count = get(selectionCountState)

    return count > 10
  },
})

export const modalBulkUnsubscribeCanWipeDataState = selector<boolean>({
  key: 'recipientsList_modalBulkUnsubscribeCanWipeDataState',
  get: ({ get }) => {
    const count = get(selectionCountState)

    return count === 1
  },
})

//  bulk add to group

export const modalBulkAddToGroupState = atom<ModalBulkAddToGroupState>({
  key: 'recipientsList_modalBulkAddToGroupState',
  default: {
    show: false,
    addToGroupId: undefined,
    isAddToGroupIdDirty: false,
    optRemoveFromGroups: false,
    removeFromGroupsMode: 'all',
    optMoveRandom: false,
    moveRandomAmount: 1,
    isMoveRandomAmountDirty: false,
    optSetAsTestGroup: false,
    searchGroups: [],
  },
})

export const modalBulkAddToGroupCanSubmitState = selector<boolean>({
  key: 'recipientsList_modalBulkAddToGroupCanSubmitState',
  get: ({ get }) => {
    const { addToGroupId, optMoveRandom, moveRandomAmount } = get(
      modalBulkAddToGroupState,
    )

    const selectionCount = get(selectionCountState)

    const isRandomValid = optMoveRandom
      ? moveRandomAmount !== undefined &&
        moveRandomAmount > 0 &&
        moveRandomAmount <= selectionCount
      : true

    return addToGroupId !== undefined && isRandomValid
  },
})

export const modalBulkAddToGroupSelectedCountState = selector<number>({
  key: 'recipientsList_modalBulkAddToGroupSelectedCountState',
  get: ({ get }) => {
    const selectionCount = get(selectionCountState)
    const { optMoveRandom, moveRandomAmount } = get(modalBulkAddToGroupState)

    const isRandomValid = optMoveRandom
      ? moveRandomAmount !== undefined &&
        moveRandomAmount > 0 &&
        moveRandomAmount <= selectionCount
      : true

    return optMoveRandom && isRandomValid && moveRandomAmount !== undefined
      ? moveRandomAmount
      : selectionCount
  },
})

//  bulk remove from group

export const modalBulkRemoveFromGroupsState =
  atom<ModalBulkRemoveFromGroupsState>({
    key: 'recipientsList_modalBulkRemoveFromGroupsState',
    default: {
      show: false,
      addToGroupId: undefined,
      isAddToGroupIdDirty: false,
      removeFromGroupsMode: 'search',
      moveRandomAmount: 1,
      isMoveRandomAmountDirty: false,
      optMoveRandom: false,
      optAddToGroup: false,
      optSetAsTestGroup: false,
      searchGroups: [],
    },
  })

export const modalBulkRemoveFromGroupsCanSubmitState = selector<boolean>({
  key: 'recipientsList_modalBulkRemoveFromGroupsCanSubmitState',
  get: ({ get }) => {
    const { addToGroupId, optAddToGroup, optMoveRandom, moveRandomAmount } =
      get(modalBulkRemoveFromGroupsState)

    const selectionCount = get(selectionCountState)

    const isAddValid = optAddToGroup
      ? addToGroupId !== undefined && addToGroupId > 0
      : true

    const isRandomValid = optMoveRandom
      ? moveRandomAmount !== undefined &&
        moveRandomAmount > 0 &&
        moveRandomAmount <= selectionCount
      : true

    return isAddValid && isRandomValid
  },
})

export const modalBulkRemoveFromGroupsSelectedCountState = selector<number>({
  key: 'recipientsList_modalBulkRemoveFromGroupsSelectedCountState',
  get: ({ get }) => {
    const selectionCount = get(selectionCountState)
    const { optMoveRandom, moveRandomAmount } = get(
      modalBulkRemoveFromGroupsState,
    )

    const isRandomValid = optMoveRandom
      ? moveRandomAmount !== undefined &&
        moveRandomAmount > 0 &&
        moveRandomAmount <= selectionCount
      : true

    return optMoveRandom && isRandomValid && moveRandomAmount !== undefined
      ? moveRandomAmount
      : selectionCount
  },
})

//  fake save segment

export const modalSaveSegmentState = atom<ModalSaveSegmentState>({
  key: 'recipientsList_modalSaveSegmentState',
  default: {
    show: false,
    name: '',
    nameIsDirty: false,
  },
})

export const modalDelStatsContactState = atom<ModalDelStatsContactState>({
  key: 'recipientsList_modalDelStatsContactState',
  default: {
    show: false,
    email: '',
    isDirty: false,
    requestTitle: '',
    requestDescription: '',
  },
})

export const modalDelStatsContactCanConfirmState = selector<boolean>({
  key: 'recipientsList_modalDelStatsContactCanConfirmState',
  get: ({ get }) => {
    const { email } = get(modalDelStatsContactState)

    return isValidEmail(email)
  },
})
